/**
 * This file contains the overrides for the prediction API.
 * For technical reasons the generated contracts is exposing a lot of fields as nullable.
 * That's why we have to override the types and the client functions.
 * All overrides must call the original client function to avoid breaking types.
 * Doing so we ensure to create more restrictive types but if the original types change, we will have to update the overrides.
 */

import { z } from "zod";

import { PredictionContractPart } from "../../contracts";
import { MaterialGradeSchema, PartTypeSchema } from "../../types";
import {
  customInputsCustomInputsPost,
  CustomInputsCustomInputsPostData,
  getAvailableMultiModalModelVersionMultiModalModelVersionsGet,
  GetAvailableMultiModalModelVersionMultiModalModelVersionsGetData,
  getModelDescriptionModelDescriptionGet,
  GetModelDescriptionModelDescriptionGetData,
  getModelVersionModelVersionGet,
  GetModelVersionModelVersionGetData,
  getMultiModalModelCardMultiModalModelCardGet,
  GetMultiModalModelCardMultiModalModelCardGetData,
  ModelAlias,
  Options,
  Part,
  PartLevel,
  predictFromDrawingPredictFromDrawingPost,
  PredictFromDrawingPredictFromDrawingPostData,
  PredictFromDrawingRequest,
  predictMultiModalPredictMultiModalPost,
  PredictMultiModalPredictMultiModalPostData,
  predictV2PredictV2Post,
  PredictV2PredictV2PostData,
} from "../prediction";
import {
  zHollowSquareBar,
  zHollowTube,
  zMultiModalUserInput,
  zPart,
  zPartLevel,
  zSolidRoundBar,
  zSolidSquareBar,
} from "../prediction/zod.gen";

export const PredictV2RequestSchema = z.object({
  material: MaterialGradeSchema,
  part_type: PartTypeSchema,
  process_keys: z.union([z.literal("all"), z.array(z.string()), z.null()]),
  custom_inputs: z.array(z.record(z.unknown())),
  model_key: z.string(),
  cnc_raw_dimensions: z.union([zSolidRoundBar, zHollowTube, zSolidSquareBar, zHollowSquareBar]),
  feature_json: z.array(z.record(z.unknown())).nullish(),
  part_json: zPart.nullish(),
  graph_url: z.string(),
  additional_process_names: z.array(z.string()),
  part_level: zPartLevel,
});

export type PredictV2RequestOverride = Omit<z.infer<typeof PredictV2RequestSchema>, "part_json"> & {
  part_json: PredictionContractPart | null;
};

export const postPredictV2 = <ThrowOnError extends boolean = false>(
  options: Options<
    Omit<PredictV2PredictV2PostData, "body"> & { body: PredictV2RequestOverride },
    ThrowOnError
  >,
) => predictV2PredictV2Post(options);

export const PredictMultiModalRequestSchema = z.object({
  model_key: z.string(),
  user_input: zMultiModalUserInput,
  part_level: zPartLevel,
  part_json_2d: zPart.nullable(),
  part_json_3d: zPart.nullable(),
  process_keys: z.union([z.literal("all"), z.array(z.string()), z.null()]),
  graph_url: z.string().nullable(),
});

export type PredictMultiModalRequestOverride = Omit<
  z.infer<typeof PredictMultiModalRequestSchema>,
  "part_json_2d" | "part_json_3d"
> & {
  part_json_2d: PredictionContractPart | null;
  part_json_3d: PredictionContractPart | null;
};

export const postPredictMultiModal = <ThrowOnError extends boolean = false>(
  options: Options<
    Omit<PredictMultiModalPredictMultiModalPostData, "body"> & {
      body: PredictMultiModalRequestOverride;
    },
    ThrowOnError
  >,
) => predictMultiModalPredictMultiModalPost(options);

export type PredictFromDrawingRequestOverride = {
  model_key: string;
  drawing_extraction: PredictFromDrawingRequest["drawing_extraction"];
  process_keys: "all" | Array<string> | null;
  drawing_embedding: Array<number> | null;
  feature_json: Array<{
    [key: string]: unknown;
  }> | null;
  part_json: Part | null;
  part_level: PartLevel;
};

export const postPredictFromDrawing = <ThrowOnError extends boolean = false>(
  options: Options<
    Omit<PredictFromDrawingPredictFromDrawingPostData, "body"> & {
      body: PredictFromDrawingRequestOverride;
    },
    ThrowOnError
  >,
) => predictFromDrawingPredictFromDrawingPost(options);

export const postCustomInputs = <ThrowOnError extends boolean = false>(
  options: Options<
    Omit<CustomInputsCustomInputsPostData, "body"> & {
      body: {
        model_key: string;
      };
    },
    ThrowOnError
  >,
) => customInputsCustomInputsPost(options);

export const getModelVersion = <ThrowOnError extends boolean = false>(
  options: Options<
    Omit<GetModelVersionModelVersionGetData, "query"> & {
      query: { model_key: string; model_alias?: ModelAlias; is_multi_modal?: boolean };
    },
    ThrowOnError
  >,
) => getModelVersionModelVersionGet(options);

export const getModelDescription = <ThrowOnError extends boolean = false>(
  options: Options<
    Omit<GetModelDescriptionModelDescriptionGetData, "query"> & {
      query: { model_key: string; is_multi_modal?: boolean };
    },
    ThrowOnError
  >,
) => getModelDescriptionModelDescriptionGet(options);

export const getMultiModalModelVersions = <ThrowOnError extends boolean = false>(
  options: Options<GetAvailableMultiModalModelVersionMultiModalModelVersionsGetData, ThrowOnError>,
) => getAvailableMultiModalModelVersionMultiModalModelVersionsGet(options);

export const getMultiModalModelCard = <ThrowOnError extends boolean = false>(
  options: Options<GetMultiModalModelCardMultiModalModelCardGetData, ThrowOnError>,
) => getMultiModalModelCardMultiModalModelCardGet(options);
